import { useCallback, useEffect, useLayoutEffect, useMemo } from "react";

import { Root as AccordionRoot } from "@radix-ui/react-accordion";
import { useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { Divider } from "src/base-components/Divider";
import {
  EditorAccordionItem as AccordionItem,
  accordionRootClassName,
} from "src/base-components/EditorAccordionItem";
import { CaseTaskSelector } from "src/cases/templates/CaseTaskSelector";
import { CaseWorkflowEditor } from "src/cases/templates/CaseWorkflowEditor";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { getStepById } from "src/cases/templates/formSteps";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import {
  useCurrentStatusValue,
  useTemplateVersionStatusValues,
} from "src/cases/templates/hooks";
import {
  buildBackendStatusKey,
  buildStatusLabel,
  FlattenedStatusValue,
} from "src/cases/templates/utils";
import { Tooltip } from "src/design-system/Tooltip";

const CaseStatusSidebar: React.FC<{
  statusValues: FlattenedStatusValue[];
}> = ({ statusValues }) => {
  const [currentStatusValue, setCurrentStatusValue] = useCurrentStatusValue();

  const handleKeyDown = (
    statusValue: FlattenedStatusValue,
    e: React.KeyboardEvent<HTMLLIElement>,
  ) => {
    if (e.key === "Enter") {
      setCurrentStatusValue(statusValue);
    }
  };

  return (
    <div className="w-44 shrink-0">
      <ul
        className="mb-4 flex flex-col gap-y-2 bg-white py-0"
        data-loc="status-sidebar"
      >
        {statusValues?.map((statusValue) => (
          <li
            key={statusValue._statusKey}
            className={twJoin(
              "cursor-pointer truncate whitespace-nowrap rounded-lg px-2 py-1.5",
              "hover:bg-gray-50 data-[active=true]:bg-gray-100",
            )}
            data-active={currentStatusValue === statusValue._statusKey}
            data-loc={`status-item-${buildBackendStatusKey(statusValue)}`}
            tabIndex={0}
            onClick={() => setCurrentStatusValue(statusValue)}
            onKeyDown={(event) => handleKeyDown(statusValue, event)}
          >
            <Tooltip delay={500} title={buildStatusLabel(statusValue)} asChild>
              <span className="font-inter-medium-13px text-gray-800 capitalize">
                {buildStatusLabel(statusValue)}
              </span>
            </Tooltip>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const DefineWorkflowRulesStep: React.FC = () => {
  const step = getStepById("define-workflow-rules");
  const { statusValues, getStatusValue } = useTemplateVersionStatusValues();
  const [currentStatusValue, setCurrentStatusValue] = useCurrentStatusValue();
  const { getValues, setValue } = useFormContext<CaseTemplateFormValues>();

  const statusValue = useMemo(
    () => (currentStatusValue ? getStatusValue(currentStatusValue) : undefined),
    [getStatusValue, currentStatusValue],
  );

  const statusKey = statusValue?._statusKey;

  useEffect(() => {
    // If no status is selected, default to the first available status
    if (!statusValue && statusValues.length) {
      setCurrentStatusValue(statusValues[0]);
    }
  }, [statusValue, setCurrentStatusValue, statusValues]);

  // Ensure workflow rules structure exists for the current status
  const ensureWorkflowRules = useCallback(
    (statusValue: FlattenedStatusValue) => {
      const statusKey = statusValue._statusKey;
      const workflowRules = getValues(`attributes.workflow_rules.${statusKey}`);

      if (!workflowRules) {
        setValue(`attributes.workflow_rules.${statusKey}`, {
          status: statusValue.status,
          sub_status: statusValue.subStatus ?? null,
          actions: [],
          workflow_tasks_keys: [],
        });
      } else {
        setValue(
          `attributes.workflow_rules.${statusKey}.status`,
          statusValue.status,
        );
        setValue(
          `attributes.workflow_rules.${statusKey}.sub_status`,
          statusValue.subStatus ?? null,
        );
      }
    },
    [getValues, setValue],
  );

  // Initialize on status change
  useLayoutEffect(() => {
    if (statusValue) {
      ensureWorkflowRules(statusValue);
    }
  }, [statusValue, ensureWorkflowRules]);

  return (
    <FormStepWrapper description={step.description} title={step.title}>
      <div className="relative flex gap-x-4">
        <CaseStatusSidebar statusValues={statusValues} />

        <div>
          <Divider orientation="vertical" variant="dark" />
        </div>

        <div className="min-w-0 max-w-full flex-1">
          <AccordionRoot
            key={statusKey}
            className={accordionRootClassName}
            defaultValue={["tasks-configuration", "case-actions-automations"]}
            type="multiple"
          >
            <AccordionItem
              className="p-4 pt-0"
              dataLoc="tasks-configuration-accordion"
              title="Define which tasks reviewers should complete in this status"
              value="tasks-configuration"
            >
              {statusValue && statusKey && (
                <CaseTaskSelector statusValue={statusValue} />
              )}
            </AccordionItem>
            <AccordionItem
              className="p-4 pt-0"
              dataLoc="case-actions-accordion"
              title="Select which actions reviewers can perform in this status"
              value="case-actions-automations"
            >
              {statusValue && statusKey && (
                <CaseWorkflowEditor
                  ensureWorkflowRules={ensureWorkflowRules}
                  statusValue={statusValue}
                  statusValues={statusValues}
                />
              )}
            </AccordionItem>
          </AccordionRoot>
        </div>
      </div>
    </FormStepWrapper>
  );
};
